import { GaugeIcon, MicOffIcon, MessagesSquareIcon, PencilLineIcon, TimerIcon } from "lucide-react";
import { cn } from "@util/tailwind";
import type { SpeechMetrics } from "@util/training-feedback-adapter";

type Status = "good" | "watch" | "off";

interface MetricSpec {
    label: string;
    value: string;
    target: string;
    tip: string;
    status: Status;
    icon: React.ReactNode;
}

const STATUS_DOT: Record<Status, string> = {
    good: "bg-emerald-500",
    watch: "bg-amber-500",
    off: "bg-rose-500",
};

const STATUS_VALUE: Record<Status, string> = {
    good: "text-emerald-700",
    watch: "text-amber-700",
    off: "text-rose-700",
};

function statusForWpm(wpm: number): Status {
    if (wpm >= 130 && wpm <= 165) return "good";
    if (wpm >= 110 && wpm <= 185) return "watch";
    return "off";
}

function statusForFiller(per_min: number): Status {
    if (per_min < 3) return "good";
    if (per_min < 6) return "watch";
    return "off";
}

function statusForTalkListen(ratio: string): Status {
    const m = ratio.match(/(\d+)/);
    if (!m) return "watch";
    const employeePct = parseInt(m[1], 10);
    if (employeePct >= 40 && employeePct <= 60) return "good";
    if (employeePct >= 30 && employeePct <= 70) return "watch";
    return "off";
}

function statusForMonologue(sec: number): Status {
    if (sec < 30) return "good";
    if (sec < 60) return "watch";
    return "off";
}

interface Props {
    metrics: SpeechMetrics;
    // Text/chat sessions have no spoken audio, so the "Talk : listen" ratio reads
    // as "Chat : listen". Defaults to the voice wording.
    isChat?: boolean;
}

export function SpeechCoachingStrip({ metrics, isChat = false }: Props) {
    // Voice sessions: speaking-pace + spoken-filler coaching.
    const voiceSpecs: MetricSpec[] = [
        {
            label: "Words / minute",
            value: `${metrics.wordsPerMinute}`,
            target: "130 – 160",
            tip: "Aim for 130 to 160 words per minute. Slow enough to be clear, fast enough to stay engaging.",
            status: statusForWpm(metrics.wordsPerMinute),
            icon: <GaugeIcon className="w-3.5 h-3.5" />,
        },
        {
            label: "Filler words / min",
            value: `${metrics.fillerPerMinute}`,
            target: "< 3",
            tip: "Pause silently instead of saying um, uh, or like. Silent pauses signal confidence.",
            status: statusForFiller(metrics.fillerPerMinute),
            icon: <MicOffIcon className="w-3.5 h-3.5" />,
        },
        {
            label: "Talk : listen",
            value: metrics.talkListenRatio,
            target: "40 – 60% you",
            tip: "Discovery and support calls thrive when the customer is talking at least half the time.",
            status: statusForTalkListen(metrics.talkListenRatio),
            icon: <MessagesSquareIcon className="w-3.5 h-3.5" />,
        },
        {
            label: "Longest stretch",
            value: `${metrics.longestMonologueSec}s`,
            target: "< 30s",
            tip: "Long monologues lose attention. Pass the conversation back with a question every 30 seconds.",
            status: statusForMonologue(metrics.longestMonologueSec),
            icon: <TimerIcon className="w-3.5 h-3.5" />,
        },
    ];
    // Chat/TXT sessions: no spoken audio, so speaking-pace (words/min) and spoken
    // fillers (um/uh) don't apply. Show a chat-appropriate set derived from the
    // same transcript metrics: write/read balance, typed filler phrases, and the
    // longest one-sided stretch.
    const chatSpecs: MetricSpec[] = [
        {
            label: "Chat : listen",
            value: metrics.talkListenRatio,
            target: "40 – 60% you",
            tip: "Give the customer room to explain — aim to write no more than about half of the conversation.",
            status: statusForTalkListen(metrics.talkListenRatio),
            icon: <MessagesSquareIcon className="w-3.5 h-3.5" />,
        },
        {
            label: "Filler words",
            value: `${metrics.fillerCount}`,
            target: "keep low",
            tip: "Trim filler phrases like 'basically', 'actually' or 'you know' — concise replies read as clearer and more confident.",
            status: statusForFiller(metrics.fillerPerMinute),
            icon: <PencilLineIcon className="w-3.5 h-3.5" />,
        },
        {
            label: "Longest reply stretch",
            value: `${metrics.longestMonologueSec}s`,
            target: "< 30s",
            tip: "Avoid long, dense replies — break information into shorter messages the customer can follow.",
            status: statusForMonologue(metrics.longestMonologueSec),
            icon: <TimerIcon className="w-3.5 h-3.5" />,
        },
    ];
    const specs = isChat ? chatSpecs : voiceSpecs;

    return (
        <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                How you communicated
            </p>
            <div className="space-y-2">
                {specs.map((spec) => (
                    <div
                        key={spec.label}
                        className="rounded-lg border border-gray-100 bg-white p-4 flex items-start gap-3"
                    >
                        {/* Status dot */}
                        <span
                            className={cn(
                                "w-2 h-2 rounded-full flex-shrink-0 mt-1",
                                STATUS_DOT[spec.status],
                            )}
                        />

                        {/* Label + tip stacked */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                                {spec.icon}
                                {spec.label}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                {spec.tip}
                            </p>
                            {metrics.isMocked && (
                                <span className="text-[10px] text-gray-300 font-medium">
                                    sample data
                                </span>
                            )}
                        </div>

                        {/* Value + target stacked right */}
                        <div className="flex flex-col items-end flex-shrink-0">
                            <span
                                className={cn(
                                    "text-lg font-bold tabular-nums leading-tight",
                                    STATUS_VALUE[spec.status],
                                )}
                            >
                                {spec.value}
                            </span>
                            <span className="text-[10px] text-gray-400 mt-0.5">
                                Target {spec.target}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
